// Console chrome: the SAME header and left rail as the Authoring app.
//
// Omar, 2026-08-16 (verbatim, and it overrides the design doc):
//   "do not follow the style guidelines of the prototype but you MUST follow the
//    guidelines used for the Authoring, in terms of using the left navigation,
//    the header, etc. I don't want to create a brand new style only for the super
//    admin panel."
//
// So every number below is lifted from apps/fe/src/chrome.jsx rather than chosen:
// a 52px header, a 212px rail collapsing to 56px, 32px nav rows, 16px icons,
// `var(--accent-bg)` / `var(--accent-text)` for the active row, and the same
// logo lockup. Two things differ, both deliberate and both tiny:
//
//   1. the header pill reads "Console" instead of "Authoring", and carries an
//      "Internal" marker;
//   2. the favicon and tab title differ.
//
// That pair replaces the design doc's §5 E / §6 §1 instruction to make the
// console "unmistakably different … a dark full-width header and a permanent
// Internal pill". Omar's rule wins. The residual concern those lines were
// guarding — that he will have both windows open and confuse them — is met by
// the marker, the tab title and the favicon, without a second design system to
// maintain forever.

import { useState } from "react";

/** 16px stroke icons, matching apps/fe/src/icons.jsx (strokeWidth 2, round caps). */
function Icon({
  size = 16,
  children,
}: {
  size?: number;
  children: React.ReactNode;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const Icons = {
  Building: () => (
    <Icon>
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01" />
    </Icon>
  ),
  Inbox: () => (
    <Icon>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </Icon>
  ),
  Activity: () => (
    <Icon>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </Icon>
  ),
  Image: () => (
    <Icon>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </Icon>
  ),
  // ★ A NEW GLYPH, because all four above are taken and a rail entry that
  //   borrows another screen's icon is two screens that look like one. Ticks
  //   against lines: a list of rules being applied, which is what the Course
  //   import screen is. Pure geometry, no colour — it inherits `currentColor`
  //   from `Icon`, which is what keeps the design-system guard satisfied and,
  //   more to the point, what makes the active row's colour reach it.
  Rules: () => (
    <Icon>
      <path d="M11 6h10M11 12h10M11 18h10" />
      <path d="M3 5.4l1.6 1.6L7.5 4" />
      <path d="M3 11.4l1.6 1.6L7.5 10" />
      <path d="M3 17.4l1.6 1.6L7.5 16" />
    </Icon>
  ),
  Menu: () => (
    <Icon>
      <path d="M3 12h18M3 6h18M3 18h18" />
    </Icon>
  ),
  LogOut: () => (
    <Icon>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </Icon>
  ),
} as const;

export type SurfaceId =
  | "customers"
  | "requests"
  | "imageProfiles"
  | "courseImport"
  | "activity";

export interface NavItem {
  id: SurfaceId;
  label: string;
  icon: keyof typeof Icons;
  /**
   * Set only while the surface has no real screen behind it. A rail entry whose
   * screen is an honest "not built yet" state is fine; a rail entry that looks
   * live and does nothing is a false affordance
   * (`feedback_no_false_affordance_toggles`), so the screens say so plainly.
   *
   * ⚠️ This must be REMOVED when the screen ships. It was `stage: "A" | "B"`
   * until Stage B2, which meant "Requests" kept its grey `soon` marker on the
   * day it became the most-used screen in the console — a marker nobody thinks
   * to delete, because nothing fails when it is wrong. Phrasing it as "what is
   * still missing" at least makes a stale entry read as a claim rather than as
   * a label.
   */
  notBuiltYet?: true;
}

/**
 * ★ THE ONE MAPPING FROM A SURFACE TO ITS URL.
 *
 * It exists because the two halves used to be separate string expressions —
 * the rail did `go(`/${id}`)` and the parser matched literal prefixes — which
 * agreed only for as long as every id happened to equal its path. The first id
 * that did not (`imageProfiles` vs `/image-profiles`) produced a rail entry that
 * navigated to a URL nothing parsed, so the click silently landed back on
 * Customers. Nothing throws; the screen is simply never reachable
 * (`feedback_gated_walked_editable_must_agree`).
 *
 * `Record<SurfaceId, string>` makes it total: a new surface will not compile
 * until it has a path.
 */
export const SURFACE_PATHS: Record<SurfaceId, string> = {
  customers: "/",
  requests: "/requests",
  imageProfiles: "/photography-settings",
  // ⚠️ `parseRoute` walks this map with `startsWith`, in DECLARATION ORDER, so a
  // path that is a PREFIX of another would swallow it — and the loser would be
  // whichever is declared second, silently. `/course-import` is a prefix of
  // nothing here and starts with none of the others; `course-import-rules.test`
  // asserts that property over every PAIR rather than over this one entry, so
  // the next surface cannot reintroduce it (`feedback_guard_the_invariant_not_the_list`).
  courseImport: "/course-import",
  activity: "/activity",
};

export const NAV_ITEMS: readonly NavItem[] = [
  { id: "customers", label: "Customers", icon: "Building" },
  { id: "requests", label: "Requests", icon: "Inbox" },
  // No `notBuiltYet`: there IS a real screen behind this, and it says on its own
  // face which half of the plan it is. A marker here would claim the screen does
  // not exist; the screen claiming it cannot edit yet is the honest version.
  // ⚠️ The id stays `imageProfiles` while the label and the path say
  // "Photography settings" — Omar renamed the SECTION on 2026-09-08 ("the
  // section should be called Photography settings or something similar"), and
  // renaming the internal id as well would touch six call sites and two test
  // files to change nothing anybody sees
  // (`feedback_count_the_citations_before_renaming_an_identifier`). `SURFACE_PATHS`
  // is the one mapping from id to URL, so the two cannot come apart.
  { id: "imageProfiles", label: "Photography settings", icon: "Image" },
  // Omar, 2026-09-18: "Add a Course import section, following the existing
  // Photography settings pattern for editing, saving and permissions." It sits
  // next to that screen because they are the same kind of thing — the standing
  // instructions a generation obeys — and not under a customer, because one set
  // of rules governs every import (there is a single row behind it).
  //
  // No `notBuiltYet`: the screen is real and editable.
  { id: "courseImport", label: "Course import", icon: "Rules" },
  // ⚠️ There was an "AI spend" entry here until 2026-09-08. Omar removed it:
  // "Remove the standalone 'AI spend' screen, its navigation entry, the manual
  // Google-figure entry and comparison interface." AI cost reporting now lives
  // where the question is actually asked — on the company it is about, as the
  // "AI costs" tab of that company's page.
  { id: "activity", label: "Activity", icon: "Activity", notBuiltYet: true },
];

/**
 * The name of a screen, as the left rail shows it.
 *
 * ★ WHY THIS EXISTS. On 2026-09-08 Omar renamed "Image profiles" to
 * "Photography settings". The screen, the rail and the URL were all updated —
 * and a sentence on a DIFFERENT screen went on saying *"they use the shared
 * instructions on the Image profiles screen"*, pointing at a name that no longer
 * existed anywhere. Nothing failed: the unit test for the renamed screen only
 * read that screen, and prose on another one was invisible to it.
 *
 * It was caught by the post-deploy probe, which asserts the OLD string is gone
 * from the whole bundle — not by any test. So the fix is structural rather than
 * another sweep: prose that names a screen READS THE NAME FROM HERE, and cannot
 * go stale (`feedback_one_rule_one_place`).
 */
export function surfaceLabel(id: SurfaceId): string {
  const item = NAV_ITEMS.find((i) => i.id === id);
  // `NAV_ITEMS` is checked against `SURFACE_PATHS` by a test, and both are keyed
  // by `SurfaceId`, so this cannot be reached while those agree.
  return item ? item.label : id;
}

function DynamoLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <rect width="24" height="24" rx="5" fill="var(--text-strong)" />
      <rect x="5" y="6.5" width="14" height="2" rx="1" fill="var(--accent)" />
      <rect x="5" y="11" width="14" height="2" rx="1" fill="var(--surface)" opacity="0.55" />
      <rect x="5" y="15.5" width="8" height="2" rx="1" fill="var(--surface)" opacity="0.55" />
    </svg>
  );
}

export function TopHeader({
  email,
  onSignOut,
  onToggleRail,
}: {
  email: string | null;
  onSignOut: () => void;
  onToggleRail: () => void;
}) {
  return (
    <header
      className="console-header"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "0 16px",
        background: "var(--surface)",
        borderBottom: "1px solid var(--border)",
        position: "relative",
        zIndex: 30,
      }}
    >
      <button
        className="btn ghost sm console-menu-btn focusable"
        onClick={onToggleRail}
        aria-label="Open navigation"
        style={{ marginLeft: -6 }}
      >
        <Icons.Menu />
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <DynamoLogo />
        <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>
          Dynamo
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--text-faint)",
            padding: "1px 5px",
            background: "var(--surface-inset)",
            borderRadius: 3,
            fontWeight: 500,
          }}
        >
          Console
        </span>
        {/* The whole of the "tell the two apps apart" budget, spent here. */}
        <span
          className="pill issues"
          style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}
          title="Dynamo staff only — this is not a customer-facing app"
        >
          Internal
        </span>
      </div>

      <div style={{ flex: 1 }} />

      {email && (
        <span
          className="truncate"
          style={{ fontSize: 12.5, color: "var(--text-muted)", maxWidth: 220 }}
          title={email}
        >
          {email}
        </span>
      )}
      <button className="btn sm focusable" onClick={onSignOut}>
        <Icons.LogOut />
        <span>Sign out</span>
      </button>
    </header>
  );
}

export function LeftRail({
  current,
  onNavigate,
  open,
  onClose,
}: {
  current: SurfaceId;
  onNavigate: (id: SurfaceId) => void;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <nav
      className="console-rail"
      data-open={open ? "true" : "false"}
      style={{
        background: "var(--surface)",
        borderRight: "1px solid var(--border)",
        // width lives in console.css — see the note on `.console-rail` there.
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          flex: 1,
          padding: "8px 6px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {NAV_ITEMS.map((item) => {
          const Glyph = Icons[item.icon];
          const active = current === item.id;
          return (
            <button
              key={item.id}
              className="focusable"
              onClick={() => {
                onNavigate(item.id);
                onClose();
              }}
              aria-current={active ? "page" : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "0 10px",
                height: 32,
                width: "100%",
                border: 0,
                borderRadius: "var(--radius)",
                background: active ? "var(--accent-bg)" : "transparent",
                color: active ? "var(--accent-text)" : "var(--text-muted)",
                cursor: "default",
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                fontFamily: "inherit",
              }}
            >
              <Glyph />
              <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>
              {item.notBuiltYet && (
                <span
                  className="pill pending"
                  style={{ fontSize: 9.5, padding: "0 4px" }}
                  title="Not built yet — the screen behind this is still to come"
                >
                  soon
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/** Drawer scrim, mobile only. Rendered by the shell so the rail stays dumb. */
export function RailScrim({ onClose }: { onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: "52px 0 0 0",
        background: "var(--text-strong)",
        opacity: 0.28,
        zIndex: 15,
      }}
    />
  );
}

export function useRailDrawer() {
  const [open, setOpen] = useState(false);
  return { open, toggle: () => setOpen((o) => !o), close: () => setOpen(false) };
}
